/*
 * Light or dark (step 10, round 1). Until the person picks with the toggle, the page follows the
 * system's setting, and follows it when it changes; nothing is written for them. Their pick is kept
 * under one key and wins from then on. DESIGN.md: every colour is a `light-dark()` pair switched by
 * `data-theme` on the root, which the shell sets from this.
 */
import { createContext, useEffect, useState } from "react";
import { readPreference } from "../preferences.ts";

const THEME_KEY = "leadcue-reply-theme";
const SYSTEM_DARK = "(prefers-color-scheme: dark)";

type Theme = "dark" | "light";

/** Whether the shell is dark, for what cannot follow `light-dark()`: an email frame's own document. */
export const DarkThemeContext = createContext(false);

/** The person's own pick, or null until they make one. Storage that cannot be read holds none. */
function picked(): Theme | null {
  try {
    const stored = readPreference(localStorage, THEME_KEY);
    return stored === "dark" || stored === "light" ? stored : null;
  } catch {
    return null;
  }
}

const systemDark = (): boolean =>
  typeof matchMedia !== "undefined" && matchMedia(SYSTEM_DARK).matches;

/**
 * The browser's own bar takes the colour of the bar under it, `--lcr-nav` (the phone's top bar and
 * the rail). `index.html` gives each system setting its colour before any script runs; once the page
 * knows the person's pick, both follow it, so a light pick on a dark system has a light bar.
 */
export const BROWSER_CHROME = { light: "#fbf8f2", dark: "#292620" } as const;

function useBrowserChrome(dark: boolean): void {
  useEffect(() => {
    const colour = dark ? BROWSER_CHROME.dark : BROWSER_CHROME.light;
    for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'))
      meta.content = colour;
  }, [dark]);
}

/** Whether the page is dark, and the toggle, which keeps the person's pick. */
export function useTheme(): { dark: boolean; toggle: () => void } {
  const [pick, setPick] = useState(picked);
  const [system, setSystem] = useState(systemDark);
  useEffect(() => {
    if (pick !== null || typeof matchMedia === "undefined") return;
    const list = matchMedia(SYSTEM_DARK);
    const change = () => setSystem(list.matches);
    list.addEventListener("change", change);
    return () => list.removeEventListener("change", change);
  }, [pick]);
  const dark = pick === null ? system : pick === "dark";
  useBrowserChrome(dark);
  return {
    dark,
    toggle: () => {
      const next: Theme = dark ? "light" : "dark";
      setPick(next);
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        // Storage unavailable: the pick lasts for this page, which is all it can do.
      }
    },
  };
}
